import axios from 'axios';
import { getBackendUrl } from '../../utils/backendConfig';
import type {
  LoginPayload,
  TokenResponse,
  AuthUser,
  ShopResponse,
  ShopMemberResponse,
  ConversationListResponse,
  ConversationResponse,
  MessageListResponse,
  MessageResponse,
  MessageCreate,
  PresignedUrlResponse,
  InspectionResponse,
  InspectionListResponse,
  TemplateResponse,
  InspectRequest,
  ItemPatchRequest,
  TranscriptResponse,
  SmsMessageResponse,
  SmsSendResponse,
} from '../types';

const BASE_URL = getBackendUrl() + '/api/v1';

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Token storage ────────────────────────────────────────────────────────────

const TOKEN_KEY = 'haist_shop_token';
const REFRESH_KEY = 'haist_shop_refresh';

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const refreshTokenStorage = {
  get: () => localStorage.getItem(REFRESH_KEY),
  set: (token: string) => localStorage.setItem(REFRESH_KEY, token),
  clear: () => localStorage.removeItem(REFRESH_KEY),
};

// Set both tokens together (login / signup / google / apple flows call this).
export function setTokens(access: string, refresh?: string | null): void {
  tokenStorage.set(access);
  if (refresh) refreshTokenStorage.set(refresh);
  // Notify the embed bridge (extension host) that the token changed so it can
  // push the new session up to the panel's sync engine. No-op outside the embed.
  try { window.dispatchEvent(new Event('haist-tokens-set')); } catch { /* SSR / no window */ }
}

export function clearTokens(): void {
  tokenStorage.clear();
  refreshTokenStorage.clear();
}

// ─── Interceptors ─────────────────────────────────────────────────────────────

api.interceptors.request.use((config) => {
  const token = tokenStorage.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Single in-flight refresh — concurrent 401s wait for the same promise instead
// of firing N parallel refresh calls.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refresh = refreshTokenStorage.get();
  if (!refresh) return null;
  try {
    // Bare axios call — bypasses our interceptors so a refresh 401 doesn't
    // recurse back into this same handler.
    const baseURL = api.defaults.baseURL;
    const res = await axios.post(`${baseURL}/auth/refresh`, { refresh_token: refresh });
    const access = res.data?.access_token as string | undefined;
    const newRefresh = res.data?.refresh_token as string | undefined;
    if (!access) return null;
    setTokens(access, newRefresh ?? refresh);
    return access;
  } catch {
    return null;
  }
}

// ── Auth-failure circuit breaker (fail calm, never flicker) ──────────────────
// A broken/stale backend (e.g. /auth/refresh returning 404 on an old dev deploy)
// makes every session look dead; the embed bootstrap then re-seeds the dead token
// and the shop iframe reloads forever (the "flicker"). Bounce to login at most a
// few times in a short window, then LATCH the session dead for a cooldown and STOP
// navigating — the shop rests on login instead of looping. The latch (read by
// applyEmbedBootstrap) also stops re-seeding the known-dead token. It auto-expires
// so it retries on a slow cadence, and clears on the next successful API call.
const DEAD_KEY = 'haist_session_dead';
const BOUNCE_KEY = 'haist_auth_bounces';
const DEAD_TTL_MS = 30_000;
export function isSessionDead(): boolean {
  try {
    const t = Number(sessionStorage.getItem(DEAD_KEY) || 0);
    return t > 0 && Date.now() - t < DEAD_TTL_MS;
  } catch {
    return false;
  }
}
function clearSessionDead(): void {
  try { sessionStorage.removeItem(DEAD_KEY); sessionStorage.removeItem(BOUNCE_KEY); } catch { /* ignore */ }
}
function bounceToLogin(): void {
  clearTokens();
  let bounces: number[] = [];
  try { bounces = JSON.parse(sessionStorage.getItem(BOUNCE_KEY) || '[]'); } catch { /* ignore */ }
  const now = Date.now();
  bounces = bounces.filter((t) => now - t < 6_000).concat(now);
  try { sessionStorage.setItem(BOUNCE_KEY, JSON.stringify(bounces)); } catch { /* ignore */ }
  if (bounces.length >= 3) {
    // Loop detected → latch dead + STOP navigating. ProtectedRoute renders login
    // in place (no full reload), and applyEmbedBootstrap stops re-seeding the
    // dead token, so the flicker stops cold.
    try { sessionStorage.setItem(DEAD_KEY, String(now)); } catch { /* ignore */ }
    return;
  }
  window.location.href = '/shop/login';
}

api.interceptors.response.use(
  (res) => { if (isSessionDead()) clearSessionDead(); return res; },
  async (error) => {
    const config = error.config;
    const status = error.response?.status;
    const url = config?.url ?? '';
    const isLoginEndpoint = url.includes('/auth/login');
    const isRefreshEndpoint = url.includes('/auth/refresh');

    // 401 → try one silent refresh-token refresh + retry. Only log the user
    // out if the refresh itself fails (refresh token also expired / invalid).
    if (status === 401 && !isLoginEndpoint && !isRefreshEndpoint && config && !config.__refreshed) {
      config.__refreshed = true;
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newAccess = await refreshPromise;
      if (newAccess) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${newAccess}`;
        return api(config);
      }
      // Refresh failed — bounce to login (circuit-broken so a broken backend
      // can't loop the shop into an infinite reload flicker).
      bounceToLogin();
      return Promise.reject(error);
    }

    // Refresh endpoint itself failed → hard logout (refresh token dead).
    if (status === 401 && isRefreshEndpoint) {
      bounceToLogin();
      return Promise.reject(error);
    }

    // Retry on network error or 5xx (up to 3 times with exponential backoff)
    const retryCount = config?.__retryCount || 0;
    const isRetryable = !error.response || [500, 502, 503, 504].includes(error.response?.status);
    if (isRetryable && retryCount < 3 && config && !isLoginEndpoint) {
      config.__retryCount = retryCount + 1;
      const delay = Math.min(1000 * Math.pow(2, retryCount), 8000); // 1s, 2s, 4s
      await new Promise((r) => setTimeout(r, delay));
      return api(config);
    }

    return Promise.reject(error);
  },
);

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (payload: LoginPayload) =>
    api.post<TokenResponse>('/auth/login', payload).then((r) => r.data),

  me: () => api.get<AuthUser>('/auth/me').then((r) => r.data),

  myShops: () => api.get<ShopResponse[]>('/auth/my-shops').then((r) => r.data),

  switchShop: (shopId: number) =>
    api.post<AuthUser>(`/auth/switch-shop/${shopId}`).then((r) => r.data),

  changePassword: (payload: {
    current_password: string;
    new_password: string;
  }) => api.post('/auth/change-password', payload).then((r) => r.data),

  googleAuth: (payload: { id_token: string }) =>
    api.post<TokenResponse>('/auth/google', payload).then((r) => r.data),

  signup: (payload: { user_name: string; user_email: string; password: string; phone?: string; shop_name?: string }) =>
    api.post<TokenResponse>('/auth/signup', payload).then((r) => r.data),

  updateMe: (payload: {
    name?: string;
    phone?: string;
  }) => api.patch<AuthUser>('/auth/me', payload).then((r) => r.data),
};

// ─── Shop ─────────────────────────────────────────────────────────────────────

export interface ShopStats {
  hero: {
    reports_sent: { value: number; prev: number };
    engagement_rate: { value: number; prev: number };
    preferences_submitted: { value: number; prev: number };
    items_inspected: { value: number; prev: number };
    emails_sent: { value: number; prev: number };
  };
  funnel: { step: string; count: number }[];
  recent_activity: {
    event_type: string;
    description: string;
    conversation_id: number;
    conversation_title: string;
    timestamp: string;
  }[];
  workers: {
    user_id: number;
    name: string;
    total: number;
    active: number;
    archived: number;
  }[];
}

export const shopApi = {
  get: () => api.get<ShopResponse>('/auth/shop').then((r) => r.data),

  stats: (period: string = '30d') =>
    api.get<ShopStats>('/auth/shop/stats', { params: { period } }).then((r) => r.data),

  update: (payload: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    dm_processing_enabled?: boolean;
    labor_rate_default?: number | null;
    labor_rate_tiers?: import('../types').LaborRateTier[] | null;
    parts_markup?: import('../types').PartsMarkup | null;
    tax_rate?: number | null;
    email_templates?: import('../types').EmailTemplate[] | null;
    report_config?: Record<string, unknown> | null;
    feature_flags?: Record<string, Record<string, boolean>> | null;
  }) => api.patch<ShopResponse>('/auth/shop', payload).then((r) => r.data),

  listMembers: () => api.get<ShopMemberResponse[]>('/auth/shop/members').then((r) => r.data),

  createMember: (payload: {
    email: string;
    name: string;
    roles?: string[];
    password?: string;
  }) => api.post<ShopMemberResponse>('/auth/shop/members', payload).then((r) => r.data),

  updateMember: (userId: number, payload: { roles?: string[]; is_active?: boolean }) =>
    api.patch<ShopMemberResponse>(`/auth/shop/members/${userId}`, payload).then((r) => r.data),
};

// ─── Conversations workspace (board / calendar) ──────────────────────────────
// One conversation row with the per-conversation KPIs the List / Per-Stage /
// Per-Charger boards reduce over. Mirrors the backend jobs_search row (shop-
// scoped, quote-less included) plus workflow_stage for the per-stage columns.
export interface BoardJobRow {
  conversation_id: number;
  shop_id: number;
  workflow_stage: string | null;
  has_memo: boolean;
  created_at: string | null;
  phone: string | null;
  customer_name: string;
  vehicle: string;
  total_cents: number;
  signed_status: string;
  quote_legal_safe: boolean | null;
  intake_date: string | null;
  complete_date: string | null;
  charger_names: string[];
  items_approved: number;
  items_declined: number;
  cents_approved: number;
  cents_declined: number;
  report_sent: boolean;
  opened: boolean;
  reviews_count: number;
  avg_rating: number | null;
}

// One day-bucket of aggregated KPIs for the Calendar view.
export interface CalDay {
  day: string; // YYYY-MM-DD (shop-local)
  jobs: number;
  signed_count: number;
  revenue_cents: number;
  items_approved: number;
  items_declined: number;
  cents_approved: number;
  cents_declined: number;
  reports_sent: number;
  opened: number;
  reviews_count: number;
  rating_sum: number;
}

export interface BoardParams {
  q?: string;
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  date_basis?: 'complete' | 'intake';
  charger_user_ids?: string; // comma-separated
}

// ─── Conversations ────────────────────────────────────────────────────────────

export const conversationsApi = {
  // Optional payload lets the "New RO" draft create the conversation WITH its
  // vehicle + customer in one atomic POST (backend get-or-creates the vehicle
  // from vin/plate and copies the customer fields). Bare create() (no args) is
  // unchanged — posts an empty body.
  create: (payload?: {
    vin?: string;
    plate?: string;
    year?: number;
    make?: string;
    model?: string;
    mileage?: number;
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    force_new?: boolean;
  }) =>
    api.post<ConversationResponse>('/conversations', payload).then((r) => r.data),

  list: (params?: { page_token?: string; limit?: number; search?: string; shop_id?: number }) =>
    api
      .get<ConversationListResponse>('/conversations', { params })
      .then((r) => r.data),

  // Conversations workspace data (shop-scoped): every conversation in the filter
  // window with KPIs (List / Per-Stage / Per-Charger group these client-side).
  board: (params?: BoardParams) =>
    api
      .get<{ rows: BoardJobRow[]; total: number }>('/conversations/board', { params })
      .then((r) => r.data),

  calendar: (params?: BoardParams) =>
    api.get<CalDay[]>('/conversations/calendar', { params }).then((r) => r.data),

  get: (conversationId: number) =>
    api
      .get<ConversationResponse>(`/conversations/${conversationId}`)
      .then((r) => r.data),

  patch: (
    conversationId: number,
    payload: { status?: string; title?: string; user_id?: number; user_ids?: number[] },
  ) =>
    api.patch<ConversationResponse>(`/conversations/${conversationId}`, payload).then((r) => r.data),

  delete: (conversationId: number) =>
    api.delete(`/conversations/${conversationId}`).then((r) => r.data),

  getTrimOptions: (conversationId: number) =>
    api.get<string[]>(`/conversations/${conversationId}/vehicle/trim-options`).then((r) => r.data),

  getReportUrl: (conversationId: number) =>
    api.get<{ url: string; portal_url: string }>(`/conversations/${conversationId}/report-url`).then((r) => r.data),

  getPortalUrl: (conversationId: number) =>
    api.get<{ url: string }>(`/conversations/${conversationId}/portal-url`).then((r) => r.data),

  transition: (conversationId: number, action: 'advance' | 'revert', skipSms?: boolean) =>
    api.post<{ workflow_stage: string; sms_sent: boolean }>(
      `/conversations/${conversationId}/transition`,
      { action, skip_sms: skipSms ?? false }
    ).then((r) => r.data),

  getVehicleBriefing: (conversationId: number) =>
    api.get<{
      vehicle: { year?: number; make?: string; model?: string; mileage?: number };
      common_problems: { title: string; report_count: number }[];
      recalls: { component: string; summary: string; consequence: string; campaign: string }[];
      recommendations: { title: string; description: string; urgency: string }[];
      service_history: { ro: string; date: string; name: string; cost: number }[];
    }>(`/conversations/${conversationId}/vehicle-briefing`).then((r) => r.data),

  // Conversation memos — free-form notes on the Summary card.
  memos: {
    list: (conversationId: number) =>
      api.get<MemoResponse[]>(`/conversations/${conversationId}/memos`).then((r) => r.data),
    create: (conversationId: number, payload: { content: string; category?: string }) =>
      api.post<MemoResponse>(`/conversations/${conversationId}/memos`, payload).then((r) => r.data),
    update: (conversationId: number, memoId: number, payload: { content: string }) =>
      api.patch<MemoResponse>(`/conversations/${conversationId}/memos/${memoId}`, payload).then((r) => r.data),
    remove: (conversationId: number, memoId: number) =>
      api.delete(`/conversations/${conversationId}/memos/${memoId}`).then((r) => r.data),
  },

  // LLM RO job suggestions (canned jobs + parts/labor, amounts in CENTS). With
  // persist=true the backend also upserts the conversation's single
  // 'ro_suggestion' chat message so it surfaces in chat. Bundle may be empty —
  // the backend enriches concerns/history/inspection from the DB.
  generateRoSuggestions: (
    conversationId: number,
    bundle: Record<string, unknown> = {},
    persist = false,
  ) =>
    api.post<{ conversation_id: number; result: RoSuggestionResult }>(
      `/conversations/${conversationId}/ro-suggestions:generate`,
      { bundle, persist },
    ).then((r) => r.data),
};

export interface MemoResponse {
  id: number;
  conversation_id: number;
  content: string;
  category?: string | null;
  author_user_id?: number | null;
  author_name?: string | null;
  source?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// ─── RO suggestions (amounts in CENTS) ──────────────────────────────────────
// inventory_id/cost_cents/available present only on GROUNDED (Tek) options; the
// free-form (no-Tek) path emits estimates (name + retail_cents + estimated:true).
export interface RoSuggestionPartOption {
  inventory_id?: number;
  name: string;
  part_number?: string;
  cost_cents?: number;
  retail_cents: number;
  available?: number;
  recommended: boolean;
  estimated?: boolean;
}
export interface RoSuggestionLineItem {
  role: string;
  spec_basis?: string;
  quantity: number;
  part_options: RoSuggestionPartOption[];
}
export interface RoSuggestion {
  source: 'concern' | 'declined_history' | 'maintenance' | string;
  concern_ref: string;
  title: string;
  canned_job?: { id?: number; name: string; labor_total_cents: number };
  estimated_labor_hours?: number;
  line_items: RoSuggestionLineItem[];
  estimated_total_cents: number;
  rationale: string;
}
export interface RoSuggestionResult {
  suggestions: RoSuggestion[];
  specs_used: string;
  notes: string;
}

// ─── Quote ────────────────────────────────────────────────────────────────────

export interface QuoteItemPhoto {
  url: string;
  label?: string;
  uploaded_at?: string;
  // Edited blob is canonical (`url`); pristine original kept under `original_url`.
  original_url?: string;
  edited_at?: string;
}

export interface QuoteLaborLine {
  description: string;
  hours?: string;
  rate?: string;
  total?: string;
}

export interface QuotePartLine {
  name: string;
  partNumber?: string;
  qty: number;
  cost?: string;
  retail?: string;
}

export interface QuoteSubletLine {
  name: string;
  vendor?: string;
  cost?: string;
  price: string;
}

export interface QuoteFeeLine {
  name: string;
  amount: string;
}

export interface QuoteDiscountLine {
  name: string;
  amount: string;
}

export interface QuoteItemPayload {
  label: string;
  // Flat summary totals (backward compat, auto-computed from lines)
  parts: string;
  qty: string;
  labor: string;
  sublet: string;
  fee: string;
  discount: string;
  laborHours: string;
  description: string;
  inspectionItemId?: string;
  // Line item arrays (source of truth when present)
  labors?: QuoteLaborLine[];
  partLines?: QuotePartLine[];
  subletLines?: QuoteSubletLine[];
  feeLines?: QuoteFeeLine[];
  discountLines?: QuoteDiscountLine[];
  // Media + AI
  photos?: QuoteItemPhoto[];
  audio_blob_name?: string | null;
  video_blob_name?: string | null;
  // AI suggestion only — mirror of inspection description_autofill.
  // Backend stamps this from POST /quote/items/{i}/suggest-description;
  // frontend renders QuoteSuggestionCard above the description textarea
  // and clears it on paste. Never the canonical text.
  description_autofill?: string | null;
  is_hidden?: boolean;
  state?: string;
  // Receipt-photo signature flag — true when this item has an attached
  // photo of a signed receipt (proof the customer authorized the work).
  has_signature_evidence?: boolean;
  signature_evidence_blob?: string | null;
  // Per-item authorization state machine (Phase 1)
  //   "unauthorized" — default for shops requiring signature; needs to be selected
  //   "selected"     — in current authorization batch
  //   "approved"     — customer signed off
  //   "declined"     — customer declined
  // Missing/null defaults: 'unauthorized' if shop requires sig, else 'selected'.
  item_status?: "unauthorized" | "selected" | "approved" | "declined";
  status_updated_at?: string;
  // ESTIMATE-track per-item legal-doc id; invoice_authorization_id is the
  // parallel INVOICE-track id (set when the final invoice is signed).
  authorization_id?: number;
  invoice_authorization_id?: number;
  // Per-item source_id used as the stable key for selection + auth-doc targeting
  source_id?: string;
}

export type LegalDocType = 'estimate' | 'invoice';

export interface ApprovalsBlock {
  approved_items?: { source_id?: string | number; label: string; amount: number }[];
  declined_items?: { source_id?: string | number; label: string; amount: number }[];
  approved_total?: number;
  original_total?: number;
  decline_reason?: string;
  submitted_at?: string;
  signature_method?: string | null;
  doc_type?: LegalDocType;
}

export interface QuotePayload {
  items: QuoteItemPayload[];
  description?: string;
  note?: string;
  // ESTIMATE track (pre-service authorization). invoice_approvals is the
  // parallel INVOICE track (post-service signature on the final bill).
  customer_approvals?: ApprovalsBlock | null;
  invoice_approvals?: ApprovalsBlock | null;
}

export interface QuoteAiAction {
  action: 'add' | 'update' | 'remove';
  index?: number;
  label?: string;
  parts?: string;
  qty?: string;
  labor?: string;
  sublet?: string;
  fee?: string;
  discount?: string;
  laborHours?: string;
  description?: string;
}

export interface QuoteAiChatResponse {
  actions: QuoteAiAction[];
  description_update?: string | null;
  note_update?: string | null;
}

export const quoteApi = {
  get: (conversationId: number) =>
    api.get<QuotePayload>(`/conversations/${conversationId}/quote`).then((r) => r.data),

  upsert: (conversationId: number, payload: QuotePayload) =>
    // replace=true: payload IS the quote. Web always holds full state, so
    // delta-merge would only invite the label-rename duplicate-append bug.
    api.put<QuotePayload>(`/conversations/${conversationId}/quote`, { ...payload, replace: true }).then((r) => r.data),

  aiChat: (conversationId: number, prompt: string, imageBlobNames?: string[]) =>
    api.post<QuoteAiChatResponse>(`/conversations/${conversationId}/quote/ai-chat`, { prompt, image_blob_names: imageBlobNames ?? [] }).then((r) => r.data),

  importFromShop: (conversationId: number) =>
    api.post<{ items: QuotePayload['items']; source: string | null; description: string; ro_number: string | null }>(
      `/conversations/${conversationId}/quote/import-from-shop`
    ).then((r) => r.data),

  inspectItem: (conversationId: number, itemIdx: number, blobNames: string[], audioBlobName?: string, videoBlobName?: string) =>
    api.post<{ conversation_id: number; item_idx: number; state: string; message: string }>(
      `/conversations/${conversationId}/quote/items/${itemIdx}/inspect`,
      { blob_names: blobNames, audio_blob_name: audioBlobName, video_blob_name: videoBlobName },
    ).then((r) => r.data),

  generateDescription: (conversationId: number, itemIdx: number) =>
    api.post<{ conversation_id: number; item_idx: number; state: string }>(
      `/conversations/${conversationId}/quote/items/${itemIdx}/generate-description`,
    ).then((r) => r.data),

  // Suggestion-card flavor (mirror of inspection description_autofill):
  // writes to quote.items[idx].description_autofill instead of overwriting
  // `description`. Frontend renders a QuoteSuggestionCard above the textarea
  // for the writer to click-to-paste or dismiss.
  suggestDescription: (conversationId: number, itemIndex: number) =>
    api.post<{ conversation_id: number; item_index: number; state: string }>(
      `/conversations/${conversationId}/quote/items/${itemIndex}/suggest-description`,
    ).then((r) => r.data),

  patchItem: (conversationId: number, itemIdx: number, patch: Partial<QuoteItemPayload>) =>
    api.patch<{ conversation_id: number; item_idx: number; item: QuoteItemPayload }>(
      `/conversations/${conversationId}/quote/items/${itemIdx}`,
      patch,
    ).then((r) => r.data),

  selectItems: (conversationId: number, sourceIds: string[], select: boolean) =>
    api.patch<{ ok: boolean; updated: number; rejected: string[]; shop_requires_signature: boolean }>(
      `/conversations/${conversationId}/quote/items/select`,
      { source_ids: sourceIds, select },
    ).then((r) => r.data),

  rollbackItemStatus: (conversationId: number, sourceIds: string[], target: "selected" | "unauthorized") =>
    api.patch<{ ok: boolean; updated: number; rejected: string[] }>(
      `/conversations/${conversationId}/quote/items/status`,
      { source_ids: sourceIds, target },
    ).then((r) => r.data),

  changeItemDecision: (conversationId: number, sourceIds: string[], decision: "approved" | "declined") =>
    api.patch<{ ok: boolean; updated: number; rejected: string[] }>(
      `/conversations/${conversationId}/quote/items/change-decision`,
      { source_ids: sourceIds, decision },
    ).then((r) => r.data),

  revokeItemAuth: (conversationId: number, sourceIds: string[]) =>
    api.patch<{ ok: boolean; updated: number; rejected: string[] }>(
      `/conversations/${conversationId}/quote/items/revoke-auth`,
      { source_ids: sourceIds },
    ).then((r) => r.data),

  sendForApproval: (conversationId: number, sourceIds: string[], docType: LegalDocType = 'estimate') =>
    api.post<{ ok: boolean; marked: number; rejected: string[] }>(
      `/conversations/${conversationId}/quote/send-for-approval`,
      { source_ids: sourceIds, doc_type: docType },
    ).then((r) => r.data),

  emailShare: (
    conversationId: number,
    payload: { to: string; subject?: string; body?: string },
  ) =>
    api.post<{ ok: boolean; report_url: string }>(
      `/conversations/${conversationId}/quote/email-share`,
      payload,
    ).then((r) => r.data),

  paperAuthorization: (
    conversationId: number,
    signatureEvidenceBlob: string,
    decisions: Record<string, 'approved' | 'declined'>,
    authorizerName?: string,
    docType: LegalDocType = 'estimate',
  ) =>
    api.post<{ ok: boolean; authorization_id: number; applied: Record<string, string>; rejected: string[] }>(
      `/conversations/${conversationId}/quote/paper-authorization`,
      { signature_evidence_blob: signatureEvidenceBlob, decisions, authorizer_name: authorizerName, doc_type: docType },
    ).then((r) => r.data),

  signatureCheck: (conversationId: number, blobName: string, mimeType?: string) =>
    api.post<{
      signature_detected: boolean;
      signature_box: [number, number, number, number] | null;
      page_w: number;
      page_h: number;
      confidence: number | null;
    }>(
      `/conversations/${conversationId}/quote/signature-check`,
      { blob_name: blobName, mime_type: mimeType || 'image/jpeg' },
    ).then((r) => r.data),

  digitalSignaturePending: (
    conversationId: number,
    decisions: Record<string, 'approved' | 'declined'>,
    authorizerName?: string,
  ) =>
    api.post<{ ok: boolean; authorization_id: number; status: string; intent: Record<string, string> }>(
      `/conversations/${conversationId}/quote/digital-signature-pending`,
      { decisions, authorizer_name: authorizerName },
    ).then((r) => r.data),

  getRecommendations: (conversationId: number) =>
    api.get<{ recommendations: QuoteRecommendation[] }>(
      `/conversations/${conversationId}/quote/recommendations`,
    ).then((r) => r.data),

  revokeApprovals: (
    conversationId: number,
    opts?: { reason?: string; source_ids?: string[]; doc_type?: LegalDocType },
  ) =>
    api.post<QuotePayload>(
      `/conversations/${conversationId}/quote/approvals/revoke`,
      opts ?? {},
    ).then((r) => r.data),

  // Tek RO authorizations imported into HAIST (push-only from extension).
  // Returns newest first; UI uses [0] as the active signed authorization.
  listTekAuthorizations: (conversationId: number) =>
    api.get<TekAuthorization[]>(
      `/conversations/${conversationId}/quote/tek-authorizations`,
    ).then((r) => r.data),

  // Unified legal-documents list: HAIST paper/digital sigs + portal sig +
  // Tek RO authorizations, with per-doc items + totals + file URL.
  listLegalDocuments: (conversationId: number) =>
    api.get<{ documents: LegalDocument[] }>(
      `/conversations/${conversationId}/quote/legal-documents`,
    ).then((r) => r.data.documents),
};

export interface LegalDocumentItem {
  source_id: string;
  label: string;
  decision: 'approved' | 'declined' | null;
  amount: number;
  // A newer doc covers this source_id — this line is stale (audit only).
  superseded?: boolean;
}

export interface LegalDocument {
  id: string;
  source: 'haist' | 'tek';
  doc_type?: LegalDocType | null;
  method: string | null;
  status: string | null;
  signature_detected: boolean;
  file_url: string | null;
  authorizer_name: string | null;
  signed_at: string | null;
  ro_number: string | number | null;
  item_count: number;
  total: number;
  items: LegalDocumentItem[];
  // Every item is covered by a newer doc — fully superseded (kept for audit).
  superseded?: boolean;
}

export interface TekAuthorization {
  id: number;
  tek_authorization_id: number;
  tek_repair_order_id: number;
  method: string | null;
  method_detail: string | null;
  authorizer_name: string | null;
  signature_pdf_blob_name: string | null;
  signed_at: string | null;
  ip: string | null;
  ro_total_cents: number | null;
  decline_all: boolean;
  all_pending_declined: boolean;
  digital: boolean;
  verbal: boolean;
  hmac: string | null;
  imported_at: string | null;
}

export interface QuoteRecommendation {
  label: string;
  description: string;
  urgency: string;
  source: string;
  confidence: number;
  related_to?: string;
  estimated_parts?: number | null;
  estimated_labor?: number | null;
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export const messagesApi = {
  list: (conversationId: number, params?: { limit?: number }) =>
    api
      .get<MessageListResponse>(`/conversations/${conversationId}/messages`, {
        params: { ...params, partial: 1 },
      })
      .then((r) => r.data),

  send: (payload: MessageCreate) =>
    api
      .post<MessageResponse>('/conversations:sendMessage', payload)
      .then((r) => r.data),
};

// ─── Storage (presigned URL upload) ──────────────────────────────────────────

// Resolve a content type the backend presigned-url validator accepts. Browsers
// sometimes report '' or 'application/octet-stream' for videos (and some files) —
// the backend rejects those (400). Fall back to the file extension. The SAME ct
// MUST be used for the presigned request AND the PUT header: the upload URL is
// signed with that content_type, so a mismatch fails the GCS signature check.
const EXT_CONTENT_TYPE: Record<string, string> = {
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  gif: 'image/gif', heic: 'image/heic', heif: 'image/heif',
  m4a: 'audio/mp4', mp3: 'audio/mpeg', pdf: 'application/pdf',
};
function resolveContentType(file: File): string {
  const t = (file.type || '').toLowerCase();
  if (t && t !== 'application/octet-stream') return t;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return EXT_CONTENT_TYPE[ext] || t || 'application/octet-stream';
}

export const storageApi = {
  getPresignedUrl: (contentType: string) =>
    api
      .post<PresignedUrlResponse>('/storage/presigned-url', { content_type: contentType })
      .then((r) => r.data),

  uploadToPresignedUrl: async (file: File): Promise<{ blob_name: string; public_url: string }> => {
    const contentType = resolveContentType(file);
    const { upload_url, blob_name, public_url } = await storageApi.getPresignedUrl(contentType);
    const res = await fetch(upload_url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.error('[UPLOAD] failed', { status: res.status, url: upload_url.split('?')[0], fileType: contentType, fileSize: file.size, body });
      throw new Error(`Image upload failed (${res.status})`);
    }
    return { blob_name, public_url };
  },
};

// ─── Vehicle Lookup ───────────────────────────────────────────────────────────

export const vehicleApi = {
  textLookup: (payload: { plate?: string; state?: string; vin?: string; conversation_id: number }) =>
    api.post('/vehicle/text-lookup', payload).then((r) => r.data),

  patch: (conversationId: number, payload: {
    mileage?: number | null;
    first_name?: string | null;
    last_name?: string | null;
    phone?: string | null;
    sub_phones?: string[] | null;
    email?: string | null;
    make?: string | null;
    model?: string | null;
    year?: number | null;
    plate?: string | null;
    state?: string | null;
  }) => api.patch<import('../types').Vehicle>(`/conversations/${conversationId}/vehicle`, payload).then((r) => r.data),

  // Cascading Year → Make → Model option lists for intake dropdowns (EPA trims catalog).
  ymmYears: () => api.get<number[]>('/vehicle/ymm/years').then((r) => r.data),
  ymmMakes: (year: number) => api.get<string[]>('/vehicle/ymm/makes', { params: { year } }).then((r) => r.data),
  ymmModels: (year: number, make: string) =>
    api.get<string[]>('/vehicle/ymm/models', { params: { year, make } }).then((r) => r.data),

  customerInfoLookup: (payload: { blob_name: string; conversation_id: number }) =>
    api.post<{ detected: boolean; first_name?: string | null; last_name?: string | null; phone?: string | null; sub_phones?: string[] | null; email?: string | null; confidence: number; conversation_id: number }>('/vehicle/customer-info-lookup', payload).then((r) => r.data),

  selectTrim: (conversationId: number, trim: string) =>
    api.post<{ status: string; trim: string; make: string; model: string; year: number; specs: any }>(`/conversations/${conversationId}/vehicle/select-trim`, { trim }).then((r) => r.data),

  getHistory: (conversationId: number) =>
    api.get<{
      sources: Array<{
        name: string;
        type: string;
        customer?: { name?: string; phone?: string; since?: string; last_visit?: string; ytd_spend?: number } | null;
        owner_count?: number;
        title_status?: string;
        records: Array<{ date: string; description: string; mileage?: string | null; total?: number | null; ro_number?: string | null; source?: string; origins?: string[]; lines?: Array<{ type: string; description: string; amount?: number | null }>; is_current?: boolean }>;
      }>;
      mileage_history: Array<{ date: string; mileage: number }>;
    }>(`/conversations/${conversationId}/vehicle/history`).then((r) => r.data),
};

// ─── OEM Diagrams ─────────────────────────────────────────────────────────────

export const diagramsApi = {
  search: (params: { make: string; query?: string; section?: string; limit?: number }) =>
    api.get<{ diagrams: any[] }>('/vehicle/diagrams', { params }).then((r) => r.data),

  laborEstimate: (params: { make: string; part: string; section?: string }) =>
    api.get<{ estimate: any }>('/vehicle/diagrams/labor-estimate', { params }).then((r) => r.data),

  stats: (make?: string) =>
    api.get<any>('/vehicle/diagrams/stats', { params: make ? { make } : {} }).then((r) => r.data),
};

// ─── Vehicle Inspection ───────────────────────────────────────────────────────

export const inspectionApi = {
  list: (params: { vehicle_id?: number; conversation_id?: number }) =>
    api
      .get<InspectionListResponse>('/vehicle/inspection/list', { params })
      .then((r) => r.data),

  get: (inspectionId: number) =>
    api
      .get<InspectionResponse>(`/vehicle/inspection/${inspectionId}`)
      .then((r) => r.data),

  getTemplates: () =>
    api
      .get<{ templates: TemplateResponse[] }>('/vehicle/inspection/templates')
      .then((r) => r.data.templates),

  start: (payload: { conversation_id: number; vehicle_id?: number; template_id?: number; mode?: string }) =>
    api
      .post<InspectionResponse>('/vehicle/inspection/start', payload)
      .then((r) => r.data),

  previewReport: (inspectionId: number) =>
    api
      .get<{ html: string }>(`/vehicle/inspection/${inspectionId}/preview-report`)
      .then((r) => r.data),

  printReport: (inspectionId: number) =>
    api
      .get<{ html: string }>(`/vehicle/inspection/${inspectionId}/print-report`)
      .then((r) => r.data),

  shareReport: (inspectionId: number) =>
    api
      .post<{ url: string }>(`/vehicle/inspection/${inspectionId}/share-report`)
      .then((r) => r.data),

  switchMode: (inspectionId: number, mode: string) =>
    api
      .post<InspectionResponse>(`/vehicle/inspection/${inspectionId}/switch-mode`, { mode })
      .then((r) => r.data),

  switchTemplate: (inspectionId: number, template_id: number) =>
    api
      .post<InspectionResponse>(`/vehicle/inspection/${inspectionId}/switch-template`, { template_id })
      .then((r) => r.data),

  // Inspect an item (attach photo blobs, triggers AI analysis)
  inspectItem: (itemId: number, payload: InspectRequest) =>
    api
      .post(`/vehicle/inspection/items/${itemId}/inspect`, payload)
      .then((r) => r.data),

  // Update item fields (notes, state, condition, etc.)
  patchItem: (itemId: number, payload: ItemPatchRequest) =>
    api
      .patch(`/vehicle/inspection/items/${itemId}`, payload)
      .then((r) => r.data),

  // Fetch (and cache) price estimate for an item via LLM
  getPriceEstimate: (itemId: number) =>
    api
      .get<{ parts_range: { min: number; max: number } | null; labor_range: { min: number; max: number } | null }>(`/vehicle/inspection/items/${itemId}/price-estimate`)
      .then((r) => r.data),

  // AI Inspection: bulk analyze photos/video
  aiInspect: (inspectionId: number, payload: { photo_blob_names: string[]; audio_blob_names?: string[]; video_blob_name?: string; text?: string }) =>
    api
      .post<{ batch_id: number; status: string }>(`/vehicle/inspection/${inspectionId}/ai-inspect`, payload)
      .then((r) => r.data),

  // AI Inspection: get batch status
  aiInspectBatch: (inspectionId: number, batchId: number) =>
    api
      .get<{ id: number; status: string; items_created?: number; items_merged?: number; error?: string }>(`/vehicle/inspection/${inspectionId}/ai-inspect/${batchId}`)
      .then((r) => r.data),

  // AI Inspection: chat — backend now queues into a BackgroundTask and
  // returns {job_id, status}. The wrapper below polls until done so
  // existing callsites can keep their `await api.aiInspectChat(...)`
  // shape and get the final chat-result back.
  startAiInspectChat: (inspectionId: number, payload: { prompt: string; photo_blob_names?: string[]; audio_blob_names?: string[]; video_blob_name?: string; persist_to_chat?: boolean; silent?: boolean }) =>
    api
      .post<{ job_id: number; status: string; result: any; error: string | null }>(`/vehicle/inspection/${inspectionId}/ai-inspect-chat`, payload)
      .then((r) => r.data),

  getAiInspectChat: (inspectionId: number, jobId: number) =>
    api
      .get<{ job_id: number; status: string; result: any; error: string | null }>(`/vehicle/inspection/${inspectionId}/ai-inspect-chat/${jobId}`)
      .then((r) => r.data),

  aiInspectChat: async (
    inspectionId: number,
    payload: { prompt: string; photo_blob_names?: string[]; audio_blob_names?: string[]; video_blob_name?: string; persist_to_chat?: boolean; silent?: boolean },
  ): Promise<{ actions_applied: number; items_added: number; items_updated: number; items_removed: number; vehicle_detection?: any; actions?: Array<{ action: string; item_id?: number; part_name?: string }> }> => {
    const job = await inspectionApi.startAiInspectChat(inspectionId, payload);
    // Poll until terminal. Backoff: 1s, 1.5s, 2s, capped at 3s. Cap
    // total wait at 90s — chat runs typically finish in <10s.
    const started = Date.now();
    let delay = 1000;
    let current = job;
    while (current.status !== 'completed' && current.status !== 'failed') {
      if (Date.now() - started > 90_000) throw new Error('ai-inspect-chat timed out after 90s');
      await new Promise((res) => setTimeout(res, delay));
      delay = Math.min(delay * 1.5, 3000);
      current = await inspectionApi.getAiInspectChat(inspectionId, job.job_id);
    }
    if (current.status === 'failed') {
      throw new Error(current.error || 'ai-inspect-chat failed');
    }
    return current.result;
  },

  // Transcription RPC (long-running, DB-backed cache). Start (or get the cached)
  // Cloud Speech-to-Text transcript for a media blob — word timestamps.
  startTranscribe: (payload: { blob_name: string; item_id?: number; media_kind?: 'audio' | 'video' }) =>
    api.post<TranscriptResponse>('/vehicle/inspection/transcribe', payload).then((r) => r.data),

  getTranscript: (transcriptId: number) =>
    api.get<TranscriptResponse>(`/vehicle/inspection/transcribe/${transcriptId}`).then((r) => r.data),

  // Kick off transcription and poll until ready. Same backoff as aiInspectChat.
  // A completed blob comes back on the first call (cache hit) — no polling.
  transcribeAndPoll: async (payload: { blob_name: string; item_id?: number; media_kind?: 'audio' | 'video' }): Promise<TranscriptResponse> => {
    let current = await inspectionApi.startTranscribe(payload);
    const started = Date.now();
    let delay = 1000;
    while (current.status !== 'completed' && current.status !== 'failed') {
      if (Date.now() - started > 120_000) throw new Error('transcribe timed out after 120s');
      await new Promise((res) => setTimeout(res, delay));
      delay = Math.min(delay * 1.5, 3000);
      current = await inspectionApi.getTranscript(current.id);
    }
    if (current.status === 'failed') throw new Error(current.error || 'transcribe failed');
    return current;
  },

  // BAR audit log — fire-and-forget when an AI suggestion card is
  // shown / accepted / dismissed. Returns 204.
  postSuggestionEvent: (itemId: number, payload: { kind: 'shown' | 'accepted' | 'dismissed'; suggestion_sha256?: string | null }) =>
    api.post(`/vehicle/inspection/items/${itemId}/suggestion-event`, payload).then(() => undefined).catch(() => undefined),

  // Add custom inspection item
  addItem: (inspectionId: number, payload: { part_name: string; category?: string; description?: string; state?: string }) =>
    api
      .post<InspectionResponse>(`/vehicle/inspection/${inspectionId}/items`, payload)
      .then((r) => r.data),


  // Email report directly to customer
  emailReport: (inspectionId: number, payload: { to: string; subject?: string; template_id?: string; template_body?: string }) =>
    api
      .post<{ success: boolean }>(`/vehicle/inspection/${inspectionId}/email-report`, payload)
      .then((r) => r.data),

  // SMS report directly to customer
  smsReport: (inspectionId: number, to: string) =>
    api
      .post<{ success: boolean; report_url: string }>(`/vehicle/inspection/${inspectionId}/sms-report`, { to })
      .then((r) => r.data),
};

// ─── Inspection video segments (play + download per recorded block) ─────────

export interface InspectionVideoSegment {
  id: number;
  video_id: number;
  inspection_id: number;
  segment_index: number;
  blob_name: string;
  content_type: string;
  duration_ms?: number | null;
  start_ms?: number | null;
  status: string;
  transcript_text?: string | null;
  // Directly-playable CDN URL for this block.
  public_url?: string | null;
}

export interface InspectionVideo {
  id: number;
  inspection_id: number;
  status: string; // recording | finalizing | ready | failed
  duration_ms?: number | null;
  video_url?: string | null;
  playlist_url?: string | null;
  segments: InspectionVideoSegment[];
}

export const inspectionVideoApi = {
  // All videos for an inspection (newest first); [] when none. Each carries its
  // recorded segments with a directly-playable public_url.
  listVideos: (inspectionId: number) =>
    api.get<InspectionVideo[]>(`/vehicle/inspection/${inspectionId}/videos`).then((r) => r.data),

  // Signed download URL for one segment block (content-disposition: attachment).
  segmentDownload: (inspectionId: number, segmentId: number) =>
    api
      .get<{ status: string; download_url?: string | null; expires_in?: number | null }>(
        `/vehicle/inspection/${inspectionId}/video/segments/${segmentId}:download`,
      )
      .then((r) => r.data),
};

// ─── Price agent (vendor pricing via shop-side Chrome extension) ────────────

export interface VendorJobView {
  id: number;
  vendor: string;
  status: 'queued' | 'in_progress' | 'needs_input' | 'done' | 'failed' | 'expired';
  question?: string | null;
  result?: {
    sku?: string;
    brand?: string;
    name?: string;
    price?: number;
    available?: boolean;
    source_url?: string;
    notes?: string;
  } | null;
  error?: string | null;
  updated_at: string;
}

export interface PriceLookupView {
  id: number;
  status: 'open' | 'applied' | 'cancelled';
  part_name: string;
  vehicle: Record<string, unknown>;
  applied_vendor?: string | null;
  applied_result?: Record<string, unknown> | null;
  vendor_jobs: VendorJobView[];
  created_at: string;
}

export const priceLookupApi = {
  create: (payload: {
    conversation_id: number;
    quote_item_idx: number;
    part_name: string;
    part_hint?: string;
    vendors?: string[];
  }) => api.post<PriceLookupView>('/price-lookups', payload).then((r) => r.data),

  get: (lookupId: number) =>
    api.get<PriceLookupView>(`/price-lookups/${lookupId}`).then((r) => r.data),

  answer: (lookupId: number, jobId: number, answer: string) =>
    api.post<PriceLookupView>(`/price-lookups/${lookupId}/answer`, { job_id: jobId, answer }).then((r) => r.data),

  apply: (lookupId: number, vendorJobId: number) =>
    api.post<PriceLookupView>(`/price-lookups/${lookupId}/apply`, { vendor_job_id: vendorJobId }).then((r) => r.data),

  cancel: (lookupId: number) =>
    api.post<PriceLookupView>(`/price-lookups/${lookupId}/cancel`).then((r) => r.data),
};

export interface PriceAgentSettings {
  enabled: boolean;
  last_seen_at: string | null;
}

export const priceAgentSettingsApi = {
  get: () => api.get<PriceAgentSettings>('/price-agent/settings').then((r) => r.data),
  update: (payload: { enabled?: boolean }) =>
    api.patch<PriceAgentSettings>('/price-agent/settings', payload).then((r) => r.data),
};

// ─── SMS ─────────────────────────────────────────────────────────────────────

export const smsApi = {
  send: (payload: { to: string; body: string; conversation_id?: number; inspection_id?: number }) =>
    api.post<SmsSendResponse>('/sms/send', payload).then((r) => r.data),

  list: (params?: { conversation_id?: number; inspection_id?: number; phone?: string; limit?: number }) =>
    api.get<SmsMessageResponse[]>('/sms/messages', { params }).then((r) => r.data),

  compose: (conversationId: number, params?: { prompt?: string; channel?: string }) =>
    api.post<{ message: string; error?: string }>(
      `/conversations/${conversationId}/compose`, params || {},
    ).then((r) => r.data),

  threads: (params?: { search?: string; limit?: number }) =>
    api.get<{ threads: { phone: string; name: string; last_message: string; last_message_at: string; message_count: number; conversation_id?: number; direction?: string }[] }>('/sms/threads', { params }).then((r) => r.data),
};
